import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Scroll, RefreshCw, Filter } from "lucide-react";
import { formatDate } from "@/lib/constants";

const ACTIONS = ["create", "update", "delete", "share", "advance_stage", "convert", "life_event"];
const ENTITIES = ["user", "client", "prospect", "plan", "advice", "appointment", "execution_task", "review", "scheduling_config", "availability", "risk_profile"];

/**
 * /app/audit-log — Admin-only trail of every consequential mutation.
 * Filterable by action + entity type; each row exposes the actor, timestamp,
 * and a JSON diff (before / after) in a modal when clicked.
 */
export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (action) params.action = action;
      if (entity) params.entity_type = entity;
      const { data } = await api.get("/audit-log", { params });
      setRows(data.rows);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, entity]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline text-primary">Admin</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight flex items-center gap-2">
            <Scroll className="h-7 w-7 text-primary" /> Audit log
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Every consequential change — who did what, to which record, and when.</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-1" data-testid="audit-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
      </div>

      <Card className="border-border bg-white p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Filter:</span>
          <select value={action} onChange={(e) => setAction(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="filter-action">
            <option value="">All actions</option>
            {ACTIONS.map((a) => <option key={a}>{a}</option>)}
          </select>
          <select value={entity} onChange={(e) => setEntity(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="filter-entity">
            <option value="">All entities</option>
            {ENTITIES.map((e) => <option key={e}>{e}</option>)}
          </select>
          <span className="ml-auto text-muted-foreground">{rows.length} events</span>
        </div>
      </Card>

      <Card className="border-border bg-white p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Who</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Entity</th>
              <th className="px-4 py-3">Record ID</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No events found for this filter.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(r.timestamp)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.actor?.name || "system"}</div>
                  <div className="text-[11px] text-muted-foreground">{r.actor?.role || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${actionColor(r.action)}`}>{r.action}</span>
                </td>
                <td className="px-4 py-3 text-xs">{r.entity_type}</td>
                <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground truncate max-w-[220px]">{r.entity_id}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDetail(r)} data-testid={`audit-detail-${r.id}`}>Details</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Audit event · {detail?.action} · {detail?.entity_type}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 py-3 text-sm">
              <div><b>When:</b> {formatDate(detail.timestamp)}</div>
              <div><b>Actor:</b> {detail.actor?.name} · {detail.actor?.email} · {detail.actor?.role}</div>
              <div><b>Record:</b> <code className="rounded bg-secondary/60 px-1.5 py-0.5 text-xs">{detail.entity_id}</code></div>
              {detail.before && (
                <div>
                  <div className="mb-1 overline text-muted-foreground">Before</div>
                  <pre className="max-h-72 overflow-auto rounded-md bg-secondary/40 p-3 text-[11px]">{JSON.stringify(detail.before, null, 2)}</pre>
                </div>
              )}
              {detail.after && (
                <div>
                  <div className="mb-1 overline text-muted-foreground">After</div>
                  <pre className="max-h-72 overflow-auto rounded-md bg-secondary/40 p-3 text-[11px]">{JSON.stringify(detail.after, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const actionColor = (a) => ({
  create: "bg-accent/15 text-accent",
  update: "bg-secondary text-foreground",
  delete: "bg-primary/15 text-primary",
  share: "bg-accent/15 text-accent",
  advance_stage: "bg-accent/15 text-accent",
  convert: "bg-accent/15 text-accent",
  life_event: "bg-secondary text-foreground",
}[a] || "bg-secondary text-muted-foreground");
